import CloseIcon from '@material-design-icons/svg/filled/close.svg?react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface ReviewDialogShellProps {
  title: string;
  onClose: () => void;
  closeDisabled?: boolean;
  children: ReactNode;
  footer: ReactNode;
}

/**
 * Portal dialog for review decisions. A bottom sheet on phones, centered on
 * larger screens. Hand-rolled (not Radix) so the searchable multi-select
 * popover inside it keeps its own focus handling.
 */
export function ReviewDialogShell({
  title,
  onClose,
  closeDisabled = false,
  children,
  footer,
}: ReviewDialogShellProps) {
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={(event) => {
        // Only clicks on the backdrop itself dismiss; the card has no handler.
        if (event.target === event.currentTarget && !closeDisabled) {
          onClose();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl border border-neutral-800/60 bg-neutral-900 shadow-2xl sm:rounded-xl"
      >
        <div className="flex items-center justify-between gap-3 border-b border-neutral-800 px-5 py-4 sm:px-6">
          <h3 className="text-base font-semibold text-white">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            disabled={closeDisabled}
            aria-label="Close"
            className="rounded-md p-1 text-neutral-400 transition-colors hover:text-white disabled:opacity-50"
          >
            <CloseIcon className="size-5 fill-current" />
          </button>
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5 sm:px-6">
          {children}
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-neutral-800 px-5 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] text-sm sm:px-6">
          {footer}
        </div>
      </div>
    </div>,
    document.body,
  );
}
